import { useQuery } from "@tanstack/react-query";
import type React from "react";
import { useCallback } from "react";
import { useNavigate } from "react-router";

import type { ConstraintListItemDto, ServicePartnerCreateRequest, ServicePartnerRequest } from "@/api/types";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { createFormPermissions, toPermissionsRequest } from "@/helpers/permissions";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useUploadImage } from "@/hooks/Network/useUploadImage";
import { useBool } from "@/hooks/useBool";
import { usePermission } from "@/hooks/usePermission";
import { useSlug } from "@/hooks/useSlug";
import { canCreateNewServicePartner } from "@/modules/service-partners/permissions";
import { usePermissionQueries } from "@/queries/permissions/queryOptions";
import { useProjectConnectionQueries } from "@/queries/project-connections/queryOptions";
import { servicePartnerMutations } from "@/queries/service-partners/mutations";
import { useServicePartnersQueries } from "@/queries/service-partners/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import { type CreateOrEditServicePartnerFormValues, ServicePartnerForm } from "../components/ServicePartnerForm";

export function CreateServicePartnerPage(): React.ReactNode {
  const sessionUser = useSessionUser();
  const slug = useSlug();
  const { t } = useTranslation();
  const showFlashToast = useFlashToast();
  const navigate = useNavigate();
  const [isSubmitting, submittingHandler] = useBool(false);
  const hasPermission = usePermission();

  const servicePartnersQueries = useServicePartnersQueries();
  const permissionQueries = usePermissionQueries();

  const {
    data: categories = [],
    isFetching: isCategoriesFetching,
    error: categoriesError,
  } = useQuery(servicePartnersQueries.categories());
  const {
    data: serviceTypesData,
    isFetching: isServiceTypesFetching,
    error: serviceTypesError,
  } = useQuery({
    ...servicePartnersQueries.createTypes(),
  });

  const {
    data: roles = [],
    isFetching: isRolesFetching,
    error: rolesError,
  } = useQuery({
    ...permissionQueries.listRoles(),
    enabled: sessionUser.isAdmin,
  });
  const {
    data: defaultsServicesPermissions = [],
    isFetching: isDefaultPermissionsFetching,
    error: servicePartnersDefaultPermissionsError,
  } = useQuery({
    ...servicePartnersQueries.defaultPermissions(),
    enabled: sessionUser.isAdmin,
  });
  const connectionsQuery = { Limit: 1000, Offset: 0 };
  const projectConnectionQueries = useProjectConnectionQueries();
  const {
    data: connections,
    isFetching: isConnectionsFetching,
    error: connectionsError,
  } = useQuery({
    ...projectConnectionQueries.list(connectionsQuery),
    enabled: sessionUser.isAdmin,
  });

  const useAddServicePartnerMutation = servicePartnerMutations.useAddServicePartner();

  const { uploadFormImage } = useUploadImage();

  const onSubmit = useCallback(
    async function (data: CreateOrEditServicePartnerFormValues) {
      submittingHandler.setTrue();

      if (data.profileImage == null || data.profileImage.length == 0 || data.category?.id == null) {
        showFlashToast({
          variant: "alert",
          message: t("model.service.action.create.notification.error"),
        });

        return;
      }

      let audience: ConstraintListItemDto[] = [];
      if (!data.connection && sessionUser.isAdmin && data.type !== "iqbi") {
        audience = data.audience;
      }

      const profileImage = await uploadFormImage(data.profileImage[0]);

      if (!profileImage) return;

      const payload: ServicePartnerRequest | ServicePartnerCreateRequest = {
        title: data.title.trim(),
        subtitle: data.subtitle.trim(),
        description: data.description.trim(),
        audience,
        projectConnectionId: data.connection?.id,
        category: data.category.id,
        email: data.email?.trim(),
        phoneNumber: data.phoneNumber?.trim(),
        profileImageId: profileImage.id,
        permissions: sessionUser.isAdmin ? toPermissionsRequest(data.permissions) : [],
        type: data.type,
      };

      if (data.headerImage && data.headerImage.length > 0) {
        const headerImage = await uploadFormImage(data.headerImage[0]);

        payload.headerImageId = headerImage?.id;
      }

      try {
        const res = await useAddServicePartnerMutation.mutateAsync(payload);
        void navigate(routes.servicePartners.details({ slug, id: res.data.id }));
      } catch {
        submittingHandler.setFalse();
      }
    },
    [
      submittingHandler,
      sessionUser.isAdmin,
      uploadFormImage,
      showFlashToast,
      t,
      navigate,
      slug,
      useAddServicePartnerMutation,
    ],
  );

  const error =
    categoriesError || rolesError || servicePartnersDefaultPermissionsError || connectionsError || serviceTypesError;
  if (error) {
    return <ErrorPage error={error} />;
  }

  const isPageLoading =
    isCategoriesFetching ||
    isDefaultPermissionsFetching ||
    isRolesFetching ||
    isConnectionsFetching ||
    isServiceTypesFetching;
  if (isPageLoading) {
    return <FullSizeLoader withPadding />;
  }

  const canAccessPage = hasPermission(canCreateNewServicePartner);

  if (!canAccessPage) {
    return <ErrorPage status={403} />;
  }

  return (
    <ServicePartnerForm
      contentLanguageId={undefined}
      isEditMode={false}
      categories={categories}
      isSubmitting={isSubmitting}
      connections={connections?.items ?? []}
      allowedTypes={serviceTypesData?.allowedTypes ?? []}
      defaultValues={{
        title: "",
        subtitle: "",
        description: "",
        audience: [],
        connection: undefined,
        category: undefined,
        permissions: createFormPermissions(roles, defaultsServicesPermissions, undefined),
        email: undefined,
        headerImage: [],
        profileImage: [],
        phoneNumber: undefined,
        type: "general",
      }}
      onSubmit={onSubmit}
    />
  );
}
